/**
 * Copyright Highway9 Networks Inc.
 */
import React, { MouseEvent, useCallback, useState } from "react";
import { text } from "~/components/TextResources";
import { Icons } from "~/components/GraphicalResources";
import { ApiTokenService } from "~/services";
import ApiTokenForm from "./ApiTokenForm";
import { useInterval } from "~/hooks/useInterval";
import useErrorsAndWarnings from "~/hooks/useErrorsAndWarnings";
import { AlertDialog, ErrorsComponent, Box, ExtendedColumnDef, FilterType, Table, Button } from "@h9/ui-lib";
import moment from "moment-timezone";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { selectTimeZone } from "~/store/context/utilitySlice";
import { TimeFormat } from "~/helpers/utils";
import { getDuration } from "~/components/Utils";
import { ApiToken as Token } from "~/types/ApiToken";
import { apiTokenActions, apiTokenLoading, apiTokenOpen, fetchApiTokens, apiTokenData, apiTokenState } from "~/store/context/apiTokenSlice";
import { enqueueSnackbar } from "notistack";

const ApiToken = () => {
  const dispatch = useAppDispatch();

  const [isEditApiToken, setIsEditApiToken] = React.useState(false);
  const [selectedApiTokenIndex, setSelectedApiTokenIndex] = useState(-1);


  const [deleteOpen, setDeleteOpen] = React.useState(false);

  const data = useAppSelector(apiTokenData);
  const loading = useAppSelector(apiTokenLoading);
  const selectedRow = useAppSelector(apiTokenState);
  const open = useAppSelector(apiTokenOpen);
  const setOpen = (open: boolean) => dispatch(apiTokenActions.setDialogOpen(open));

  const { closeAlert, closeAll, errors, setErrorsAndWarnings, warnings } = useErrorsAndWarnings();

  useInterval(
    () => {
      dispatch(fetchApiTokens());
    },
    30 * 1000,
    true
  );

  const onRowSelectApiToken = (e: MouseEvent<HTMLTableRowElement>, row: Token, index: number) => {
    console.log(" row click ApiToken", row);
    setSelectedApiTokenIndex(index);
    dispatch(apiTokenActions.setSelected(row.id!));
  };


  const handleApiTokenDelete = () => {
    setDeleteOpen(true);
  };

  const openApiTokenServerDialog = (edit: boolean) => {
    setOpen(true);
    setIsEditApiToken(edit);
  };

  const deleteHandleClose = (type?: string) => {
    setDeleteOpen(false);
    if (type === "refresh") {
      dispatch(fetchApiTokens());
    }
  };

  async function deleteHandleSubmit() {
    try {
      const id = selectedRow?.id;
      id && await ApiTokenService.deleteApiToken(id);
      deleteHandleClose("refresh");
    } catch (err: any) {
      console.log(err);
      setErrorsAndWarnings(err.errors, err.warnings);
      throw err;
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <TokenTable
          loading={loading}
          data={data}
          onRowSelect={onRowSelectApiToken}
          selectedIndex={selectedApiTokenIndex}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={Icons.add}
              onClick={() => openApiTokenServerDialog(false)}
              style={{ textTransform: 'uppercase' }}
            >
              {text.add}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={Icons.edit}
              onClick={() => openApiTokenServerDialog(true)}
              disabled={selectedApiTokenIndex === -1}
              style={{ textTransform: 'uppercase' }}
            >
              {text.edit}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={Icons.delete}
              onClick={handleApiTokenDelete}
              disabled={selectedApiTokenIndex === -1}
              style={{ textTransform: 'uppercase' }}
              sx={{ textTransform: 'uppercase' }}
            >
              {text.delete}
            </Button>
          </Box>
        </TokenTable>
      </Box>

      <ApiTokenForm
        open={open}
        setOpen={setOpen}
        isEdit={isEditApiToken}
        data={selectedRow!}
      />
      {deleteOpen && (
        <AlertDialog
          open={deleteOpen}
          onConfirm={deleteHandleSubmit}
          setOpen={setDeleteOpen}
          title="Delete ApiToken Server Profile"
          message={
            <>
              <ErrorsComponent
                errorsData={errors}
                closeAlert={closeAlert}
                closeAll={closeAll}
                warningsData={warnings}
              />
              Are you sure you want to delete <b>{selectedRow?.name}</b>?
            </>
          }
          cancelButton={true}
          cancelText="Cancel"
          confirmText="Delete"
        />
      )}
    </Box>
  );
};

export default ApiToken;


type TokenTableProps = {
  loading: boolean;
  data: Token[];
  onRowSelect: (e: MouseEvent<HTMLTableRowElement>, row: Token, index: number) => void;
  selectedIndex: number;
  children: React.ReactNode;
};

function TokenTable({ loading, data, onRowSelect, selectedIndex, children }: TokenTableProps) {


  const copyToClipBoard = useCallback(((key: string, source: string) => {
    let copyText = "";
    switch (source) {
      case "string":
        copyText = key;
        break;
      default:
        console.log("default case");
    }
    navigator.clipboard.writeText(copyText);
    enqueueSnackbar("Copied Token to Clipboard", {
      variant: "success",
    })
  }), [])

  const tz = useAppSelector(selectTimeZone);


  const ApiTokencolumns = React.useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        accessorKey: "name",
        cell: ({ cell: { row } }) => (
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
            {Icons.vpnKey}{row.original.name}
          </Box>
        ),
      },
      {
        id: "creationTime",
        header: "Creation Time",
        accessorKey: "creationTime",
        cell: ({ cell: { row } }) => (
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
            {Icons.accessTimeIcon}{moment.unix(row.original.creationTime!).tz(tz).format(TimeFormat)}
          </Box>
        ),
        filterType: FilterType.TIME_RANGE,
        timezone: tz,
        timeFormat: TimeFormat
      },
      {
        id: "expiry",
        header: "Expiring On",
        accessorKey: "expiry",
        cell: ({ cell: { row } }) => (
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
            {Icons.accessTimeIcon}{moment.unix(row.original.expiry!).tz(tz).format(TimeFormat)}
          </Box>
        ),
        filterType: FilterType.TIME_RANGE,
        timezone: tz,
        timeFormat: TimeFormat
      },
      {
        id: "token",
        header: "Token",
        accessorKey: "token",
        minSize: 320,
        cell: ({ cell: { row } }) => {
          return (
            <span
              onClick={() => copyToClipBoard(row.original.token!, "string")}
            >
              *********************{row.original.token?.slice(-4)} &nbsp; {Icons.copyIcon}
            </span>
          );
        },
        disableFilter: true,
        disableSorting: true
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (row) => {
          const expiryTime = row.expiry;
          const currentTime = moment().unix();
          const days = moment.unix(expiryTime).diff(moment(), 'days');

          if (expiryTime < currentTime) {
            return "Expired";
          } else if (days < 7) {
            return "Expiring";
          } else {
            return "Valid";
          }
        },
        cell: ({ cell: { row } }) => {
          const expiryTime = row.original.expiry;

          const currentTime = moment().unix()
          const days = moment.unix(expiryTime).diff(moment(), 'days')
          let expired = false;
          let isNearExpiry = false;
          let duration = "";
          if (expiryTime < currentTime) {
            expired = true;
          } else {
            if (days < 7) {
              isNearExpiry = true;
            }
            duration = getDuration(expiryTime, currentTime)
          }
          if (expired) {
            return <span title="Expired">{Icons.red} Expired</span>;
          } else if (isNearExpiry) {
            return (
              <span title="Expiring">
                {Icons.warning} Expiring soon in {duration}
              </span>
            );
          } else {
            return (
              <span>
                {Icons.green} Valid for {duration}
              </span>
            );
          }
        },
      },
      {
        id: "description",
        header: "Description",
        accessorKey: "description",
        filterType: FilterType.TEXT,
      },
    ] satisfies ExtendedColumnDef<Token>[],
    [copyToClipBoard, tz]
  );

  return (
    <Table
      isLoading={loading}
      columns={ApiTokencolumns}
      data={data}
      onRowClick={onRowSelect}
      selectedRowIndex={selectedIndex}
      pagination={{
        defaultPageSize: 15
      }}
      search={{
        searchLeftComponent: children
      }}
    />
  );
}